import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps } from "../Button";
import Draw from "ol/interaction/Draw";
import { useCallback, useEffect, useRef } from "react";
import { DrawEvent } from "ol/interaction/Draw";
import { TextIcon } from "../../../constants/icons/TextIcon";
import { useMap } from "../../../hooks";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { FeatureFromGeojson, useFeaturesStore } from "../../../FeaturesStore";
import { positionsFromFeature } from "../../../../utils";
import { Coordinate } from "ol/coordinate";
import { makeGeojsonShape } from "../../../../utils/makeGeojsonShape";
import {
  useDrawSketchGestures,
  primaryPointerCondition,
} from "src/domains/shared/react-openlayers/Map/hooks/useDrawSketchGestures";
import { useToolGuide } from "../../useToolGuide";
import {
  DRAWING_FLAG_RELEASE_DELAY_MS,
  DRAWING_TOOL_CURSOR,
  MAP_TOOL_BUTTON,
  MAP_TOOL_LABEL_KEYS,
} from "src/domains/shared/constants/mapToolButton";
import { releaseDrawToolUi } from "./releaseDrawToolUi";

export interface TextDrawButtonProps extends ButtonProps {
  /**
   * @description You can get Multipoint feature what was made by callback function.
   */
  onEnd?: (features: FeatureFromGeojson) => void;

  /**
   * @default false
   * @description Well... Sometimes you need this drawing tool with using server waht containes DB. if 'onCanvas' set false, react-openlayer will not draw feature on canvas.
   */
  onCanvas?: boolean;
}

export function TextDrawButton({
  onEnd,
  onClick,
  onCanvas = false,
  disabled,
  ...props
}: TextDrawButtonProps) {
  const { t } = useTranslation("timeSeries");
  const map = useMap();
  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();
  const isActive = buttonId === selectedButtonId;
  const { addGeoJson } = useFeaturesStore();

  const drawRef = useRef(
    new Draw({
      condition: primaryPointerCondition,
      source: undefined,
      type: "Point",
      /** 커서 안내는 crosshair 커서와 안내 칩이 맡는다 — 빈 배열이어야 OL 기본 스케치 스타일 폴백이 걸리지 않는다 */
      style: [],
    })
  );

  useEffect(() => {
    drawRef.current = new Draw({
      condition: primaryPointerCondition,
      source: undefined,
      type: "Point",
      /** 커서 안내는 crosshair 커서와 안내 칩이 맡는다 — 빈 배열이어야 OL 기본 스케치 스타일 폴백이 걸리지 않는다 */
      style: [],
    });
  }, [onCanvas]);

  const exitDrawing = useCallback(() => {
    releaseDrawToolUi(map, drawRef.current, selectButton);
    map.setProperties({ isDrawing: false });
  }, [map, selectButton]);

  const { pointCount } = useDrawSketchGestures({
    map,
    isActive,
    kind: "text",
    getDraw: () => drawRef.current,
    onExit: exitDrawing,
  });
  useToolGuide({ isActive, kind: "text", pointCount });

  const startDrawing = () => {
    if (onClick) {
      onClick();
    }
    map.getViewport().style.cursor = DRAWING_TOOL_CURSOR;
    map.setProperties({ isDrawing: true });
    map.addInteraction(drawRef.current);
  };

  const drawing = useCallback(
    (event: DrawEvent) => {
      const feature = event.feature;
      const newPosition = positionsFromFeature(feature, true) as Coordinate;

      const newGeoJson = makeGeojsonShape(
        {
          type: "Point",
          coordinates: newPosition,
        },
        {
          type: "text",
          original_type: "point",
          isSelected: true,
        }
      );

      releaseDrawToolUi(map, drawRef.current, selectButton);
      if (onEnd) {
        onEnd(newGeoJson);
      }
      if (onCanvas) {
        addGeoJson(newGeoJson);
      }
      setTimeout(
        () => map.setProperties({ isDrawing: false }),
        DRAWING_FLAG_RELEASE_DELAY_MS
      );
    },
    [selectButton, map, onEnd, onCanvas, addGeoJson]
  );

  useEffect(() => {
    const drawingInstance = drawRef.current;
    drawingInstance.on("drawend", drawing);

    return () => {
      drawingInstance.un("drawend", drawing);
    };
  }, [drawing]);

  useEffect(() => {
    if (!isActive) {
      map.removeInteraction(drawRef.current);
    }
  }, [isActive, map]);

  return (
    <Button
      id={buttonId}
      hasPopup
      isDisabled={disabled}
      disabled={disabled}
      popupText={t(MAP_TOOL_LABEL_KEYS.text)}
      onClick={() => {
        if (isActive) {
          exitDrawing();
        } else {
          selectButton(buttonId);
          startDrawing();
        }
      }}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <TextIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
}
